import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Endpoint, EndpointInsert, EndpointUpdate } from '@/types/endpoint';

interface EndpointState {
  endpoints: Endpoint[];
  loading: boolean;
  loadEndpoints: () => Promise<void>;
  addEndpoint: (endpoint: EndpointInsert) => Promise<Endpoint | null>;
  updateEndpoint: (id: string, updates: EndpointUpdate) => Promise<void>;
  deleteEndpoint: (id: string) => Promise<void>;
  getEndpointById: (id: string) => Endpoint | undefined;
  getEndpointsBySystemId: (systemId: string) => Endpoint[];
}

export const useEndpointStore = create<EndpointState>()((set, get) => ({
  endpoints: [],
  loading: false,

  loadEndpoints: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('endpoints')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ endpoints: (data || []) as Endpoint[] });
    } catch (error) {
      console.error('Error loading endpoints:', error);
      toast.error('Failed to load endpoints');
    } finally {
      set({ loading: false });
    }
  },

  addEndpoint: async (endpoint) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const insertData = {
        name: endpoint.name,
        type: endpoint.type || 'endpoint',
        system_id: endpoint.system_id,
        site_id: endpoint.site_id,
        asset_type: endpoint.asset_type,
        category: endpoint.category,
        vendor: endpoint.vendor,
        model: endpoint.model,
        firmware: endpoint.firmware,
        ip: endpoint.ip,
        mac: endpoint.mac,
        serial: endpoint.serial,
        os: endpoint.os,
        backup: endpoint.backup,
        last_patch: endpoint.last_patch,
        eol_date: endpoint.eol_date,
        notes: endpoint.notes,
        criticality: endpoint.criticality,
        position_x: endpoint.position_x,
        position_y: endpoint.position_y,
        owner_id: endpoint.owner_id || null,
        org_id: profile.org_id,
      };

      const { data, error } = await supabase
        .from('endpoints')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      await get().loadEndpoints();
      toast.success('Endpoint created');
      return data as Endpoint;
    } catch (error: any) {
      console.error('Error creating endpoint:', error);
      toast.error(error.message || 'Failed to create endpoint');
      return null;
    }
  },

  updateEndpoint: async (id, updates) => {
    try {
      const { metadata, ...safeUpdates } = updates as any;
      const updateData = {
        ...safeUpdates,
        owner_id: safeUpdates.owner_id === undefined ? undefined : (safeUpdates.owner_id || null),
      };
      const { error } = await supabase
        .from('endpoints')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      await get().loadEndpoints();
      toast.success('Endpoint updated');
    } catch (error: any) {
      console.error('Error updating endpoint:', error);
      toast.error(error.message || 'Failed to update endpoint');
    }
  },

  deleteEndpoint: async (id) => {
    try {
      const { error } = await supabase
        .from('endpoints')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        endpoints: state.endpoints.filter(e => e.id !== id)
      }));
      toast.success('Endpoint deleted');
    } catch (error: any) {
      console.error('Error deleting endpoint:', error);
      toast.error(error.message || 'Failed to delete endpoint');
    }
  },

  getEndpointById: (id) => {
    return get().endpoints.find(e => e.id === id);
  },

  getEndpointsBySystemId: (systemId) => {
    return get().endpoints.filter(e => e.system_id === systemId);
  },
}));
